// owner/ui/Icon.js — the owner experience's outline icons (rounded, 24px grid), as signed off in the mock-ups
// (docs/02_Product/design_system/owner-app-mockups/). Decorative by default; give `label` when the icon is the
// only content of a control.

import React from 'react';

const PATHS = {
  house: <path d="M4 10.3 11.2 4.7a1.3 1.3 0 0 1 1.6 0L20 10.3V18a2.5 2.5 0 0 1-2.5 2.5h-11A2.5 2.5 0 0 1 4 18Z" />,
  houseFill: <path fill="currentColor" stroke="none" d="M3 10.6 10.9 4.5a1.8 1.8 0 0 1 2.2 0L21 10.6V18.5a2.5 2.5 0 0 1-2.5 2.5h-13A2.5 2.5 0 0 1 3 18.5Z" />,
  units: <><rect x="4" y="3.5" width="16" height="17" rx="2.5" /><path d="M9 20.5v-4.5h6v4.5M8.5 8h2M13.5 8h2M8.5 12h2M13.5 12h2" /></>,
  calendar: <><rect x="3.5" y="5" width="17" height="15.5" rx="3" /><path d="M3.5 10h17M8 3v4M16 3v4" /></>,
  wallet: <><rect x="3.5" y="6" width="17" height="13" rx="3" /><path d="M3.5 10h17M15.5 14.5h2" /></>,
  more: <><circle cx="12" cy="12" r="8.5" /><circle cx="8.3" cy="12" r=".7" fill="currentColor" /><circle cx="12" cy="12" r=".7" fill="currentColor" /><circle cx="15.7" cy="12" r=".7" fill="currentColor" /></>,
  bell: <path d="M6 16.5V11a6 6 0 0 1 12 0v5.5l1.5 2h-15ZM10 20.5a2 2 0 0 0 4 0" />,
  chev: <path d="m6 9 6 6 6-6" />,
  chevEnd: <path d="m15 6-6 6 6 6" />,
  chevStart: <path d="m9 6 6 6-6 6" />,
  back: <path d="M5 12h14M13 6l6 6-6 6" />,
  arrowTo: <path d="M19 12H5M11 6l-6 6 6 6" />,
  users: <><circle cx="9.5" cy="8" r="3.5" /><path d="M3.5 19.5a6 6 0 0 1 12 0M16 5.2a3.5 3.5 0 0 1 0 5.6M18 13.8a6 6 0 0 1 3 5.7" /></>,
  guest: <><circle cx="12" cy="8" r="3.8" /><path d="M4.5 20a7.5 7.5 0 0 1 15 0" /></>,
  gear: <><circle cx="12" cy="12" r="3" /><path d="M12 3.5v2M12 18.5v2M3.5 12h2M18.5 12h2M6 6l1.4 1.4M16.6 16.6 18 18M6 18l1.4-1.4M16.6 7.4 18 6" /></>,
  inbox: <path d="M4 13.5 6 6h12l2 7.5V18a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2ZM4 13.5h4.5a3.5 3.5 0 0 0 7 0H20" />,
  star: <path d="m12 4 2.4 4.9 5.4.8-3.9 3.8.9 5.4L12 16.4l-4.8 2.5.9-5.4-3.9-3.8 5.4-.8Z" />,
  alert: <><circle cx="12" cy="12" r="8.5" /><path d="M12 8v5M12 16h.01" /></>,
  refresh: <path d="M20 12a8 8 0 1 1-2.3-5.7M20 4v5h-5" />,
  logout: <path d="M10 4H6.5A2.5 2.5 0 0 0 4 6.5v11A2.5 2.5 0 0 0 6.5 20H10M21 12H10M17 8l4 4-4 4" />,
  pin: <><path d="M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0c0 5.4-6.5 11-6.5 11Z" /><circle cx="12" cy="10" r="2.3" /></>,
  bed: <path d="M3.5 18.5v-11M3.5 14h17v4.5M20.5 14v-2.5A2.5 2.5 0 0 0 18 9h-7.5v5M7 11.5h.01" />,
  search: <><circle cx="11" cy="11" r="6.5" /><path d="m20 20-4.3-4.3" /></>,
  globe: <><circle cx="12" cy="12" r="8.5" /><path d="M3.5 12h17M12 3.5c2.5 2.5 2.5 14.5 0 17M12 3.5c-2.5 2.5-2.5 14.5 0 17" /></>,
  hash: <path d="M5 9h14M5 15h14M10 4l-2 16M16 4l-2 16" />,
  moon: <path d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5Z" />,
  check: <path d="m5 12.5 4.5 4.5L19 7.5" />,
  clock: <><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 2" /></>,
  chat: <path d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v7a2.5 2.5 0 0 1-2.5 2.5H10l-4.5 3.5V16A2.5 2.5 0 0 1 4 13.5Z" />
};

const SIZE_CLASS = { sm: ' sm', xs: ' xs' };

const Icon = ({ name, size, label, className }) => (
  <svg
    className={`o-ic${SIZE_CLASS[size] || ''}${className ? ` ${className}` : ''}`}
    viewBox="0 0 24 24"
    aria-hidden={label ? undefined : true}
    role={label ? 'img' : undefined}
    aria-label={label}
    focusable="false"
  >
    {PATHS[name]}
  </svg>
);

export default Icon;
